import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ClipboardList, Loader, ChevronLeft, Eye, EyeOff,
  CheckCircle, X, Users, BarChart2,
} from "lucide-react";
import { oeGetExamAttempts, oeGetAttemptDetail, oeGradeAnswer, oeToggleResultVisibility, oeRecalculateAttempt } from "../api/onlineExamApi";
import { lmsGetExamById } from "../api/lmsApi";
import "../lms.css";

const getUser = () => {
  try { return JSON.parse(localStorage.getItem("userData") || "{}"); } catch { return {}; }
};

const fmtTime = (sec) => {
  if (!sec) return "—";
  const m = Math.floor(sec / 60); const s = sec % 60;
  return `${m}m ${s}s`;
};

/* ── Attempt Row ── */
function AttemptRow({ att, onView, onRecalc, recalcing }) {
  const pf = att.pass_fail;
  return (
    <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
      <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 600, color: "#111827" }}>{att.student_name || `Student #${att.student_id}`}</td>
      <td style={{ padding: "10px 14px", fontSize: 12, color: "#6b7280" }}>{att.roll_number || "—"}</td>
      <td style={{ padding: "10px 14px" }}>
        <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
          background: att.status === "submitted" ? "#dbeafe" : att.status === "graded" ? "#dcfce7" : att.status === "in_progress" ? "#fef9c3" : "#f3f4f6",
          color:      att.status === "submitted" ? "#1e40af" : att.status === "graded" ? "#166534" : att.status === "in_progress" ? "#854d0e" : "#374151" }}>
          {att.status}
        </span>
      </td>
      <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 700, color: "#111827" }}>
        {att.score != null ? `${att.score} / ${att.total_marks}` : "—"}
      </td>
      <td style={{ padding: "10px 14px", fontSize: 13, color: "#374151" }}>{att.percentage != null ? `${att.percentage}%` : "—"}</td>
      <td style={{ padding: "10px 14px" }}>
        {pf && pf !== "pending" ? (
          <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
            background: pf === "pass" ? "#dcfce7" : "#fee2e2",
            color: pf === "pass" ? "#166534" : "#991b1b" }}>
            {pf.toUpperCase()}
          </span>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, color: "#f59e0b", fontWeight: 700 }}>Pending</span>
            <button onClick={() => onRecalc(att)} disabled={recalcing === att.id} style={{
              fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 6,
              border: "1.5px solid #fde68a", background: "#fffbeb", color: "#b45309", cursor: "pointer",
            }}>
              {recalcing === att.id ? "..." : "Fix"}
            </button>
          </div>
        )}
      </td>
      <td style={{ padding: "10px 14px", fontSize: 12, color: "#6b7280" }}>{fmtTime(att.time_spent_sec)}</td>
      <td style={{ padding: "10px 14px", fontSize: 12, color: att.tab_switches > 0 ? "#ef4444" : "#9ca3af" }}>{att.tab_switches || 0}</td>
      <td style={{ padding: "10px 14px" }}>
        <button onClick={() => onView(att)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 7, border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          <Eye size={12} /> View
        </button>
      </td>
    </tr>
  );
}

const AUTO_GRADED_TYPES = ["mcq", "true_false", "fill_blank", "match_following"];
const MANUAL_TYPES      = ["long_answer", "long", "short_answer", "short"];

/* ── Grading Modal ── */
function GradingModal({ attempt, onClose, onSaved }) {
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState({});
  const [grades,  setGrades]  = useState({});
  const [tab,     setTab]     = useState("auto"); // "auto" | "manual"

  useEffect(() => {
    oeGetAttemptDetail(attempt.id)
      .then(r => {
        const ans = r.data.data.answers || [];
        setAnswers(ans);
        const init = {};
        ans.forEach(a => { init[a.id] = { marks_awarded: a.marks_awarded ?? "", is_correct: a.is_correct }; });
        setGrades(init);
        // Default to manual tab if there are manual questions
        const hasManual = ans.some(a => MANUAL_TYPES.includes(a.question_type));
        if (hasManual) setTab("manual");
      })
      .catch(() => toast.error("Failed to load answers"))
      .finally(() => setLoading(false));
  }, [attempt.id]);

  const handleGrade = async (answerId, overrides = {}) => {
    const g = { ...grades[answerId], ...overrides };
    setSaving(p => ({ ...p, [answerId]: true }));
    try {
      await oeGradeAnswer(answerId, {
        marks_awarded: Number(g.marks_awarded) || 0,
        is_correct: g.is_correct,
      });
      toast.success("Saved");
      onSaved();
    } catch { toast.error("Failed to save grade"); }
    finally { setSaving(p => ({ ...p, [answerId]: false })); }
  };

  const autoAnswers   = answers.filter(a => AUTO_GRADED_TYPES.includes(a.question_type));
  const manualAnswers = answers.filter(a => MANUAL_TYPES.includes(a.question_type));

  const autoCorrect = autoAnswers.filter(a => a.is_correct === 1).length;
  const autoWrong   = autoAnswers.filter(a => a.is_correct === 0).length;
  const autoSkipped = autoAnswers.filter(a => !a.option_id && !a.text_answer).length;

  const renderMatchAnswer = (ans) => {
    let colA = [], colB = [], answerKey = [];
    try {
      const p = JSON.parse(ans.question_text || "{}");
      colA      = Array.isArray(p.colA)    ? p.colA    : [];
      colB      = Array.isArray(p.colB)    ? p.colB    : [];
      answerKey = Array.isArray(p.answers) ? p.answers : [];
    } catch {}
    const studentSel = {};
    (ans.text_answer || "").split(",").forEach(part => {
      const m = part.trim().match(/^(\d+)[–\-]([a-z])/i);
      if (m) studentSel[Number(m[1])] = m[2].toLowerCase();
    });
    return (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {/* Correct answer key */}
        <div style={{ background: "#f0fdf4", borderRadius: 8, padding: "12px", border: "1px solid #bbf7d0" }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: "#166534", marginBottom: 8, letterSpacing: 1 }}>ANSWER KEY</div>
          {colA.map((a, i) => {
            const correctLetter = answerKey[i] || "";
            const correctText   = correctLetter ? colB[correctLetter.charCodeAt(0) - 97] || "" : "";
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5, fontSize: 12 }}>
                <span style={{ fontWeight: 600, color: "#374151", minWidth: 80 }}>{i + 1}. {a}</span>
                <span style={{ color: "#9ca3af" }}>→</span>
                <span style={{ background: "#dcfce7", color: "#166534", padding: "1px 8px", borderRadius: 20, fontWeight: 600 }}>{correctLetter}. {correctText}</span>
              </div>
            );
          })}
        </div>
        {/* Student answer */}
        <div style={{ background: "#fffbeb", borderRadius: 8, padding: "12px", border: "1px solid #fde68a" }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: "#92400e", marginBottom: 8, letterSpacing: 1 }}>STUDENT ANSWER</div>
          {Object.keys(studentSel).length === 0
            ? <em style={{ color: "#9ca3af", fontSize: 12 }}>No answer provided</em>
            : colA.map((a, i) => {
                const num       = i + 1;
                const stuLetter = studentSel[num] || "";
                const stuText   = stuLetter ? colB[stuLetter.charCodeAt(0) - 97] || "" : "";
                const isRight   = stuLetter && stuLetter === (answerKey[i] || "");
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5, fontSize: 12 }}>
                    <span style={{ fontWeight: 600, color: "#374151", minWidth: 80 }}>{num}. {a}</span>
                    <span style={{ color: "#9ca3af" }}>→</span>
                    {stuLetter
                      ? <span style={{ background: isRight ? "#dcfce7" : "#fee2e2", color: isRight ? "#166534" : "#991b1b", padding: "1px 8px", borderRadius: 20, fontWeight: 600 }}>
                          {stuLetter}. {stuText} {isRight ? "✓" : "✗"}
                        </span>
                      : <em style={{ color: "#9ca3af" }}>—</em>
                    }
                  </div>
                );
              })
          }
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "32px 16px", overflow: "auto" }}>
      <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 820, boxShadow: "0 24px 80px rgba(0,0,0,0.25)", overflow: "hidden" }}>

        {/* Gradient top bar */}
        <div style={{ height: 5, background: "linear-gradient(90deg,#4f46e5,#a855f7,#ec4899)" }} />

        {/* Header */}
        <div style={{ padding: "20px 28px 0", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 46, height: 46, borderRadius: 13, background: "linear-gradient(135deg,#4f46e5,#7c3aed)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <ClipboardList size={20} color="#fff" />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 17, color: "#111827" }}>{attempt.student_name || `Student #${attempt.student_id}`}</div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                Score: <strong style={{ color: "#111827" }}>{attempt.score} / {attempt.total_marks}</strong>
                <span style={{ margin: "0 6px", color: "#d1d5db" }}>|</span>
                {attempt.percentage}%
                <span style={{ margin: "0 6px", color: "#d1d5db" }}>|</span>
                {attempt.tab_switches > 0
                  ? <span style={{ color: "#ef4444", fontWeight: 700 }}>{attempt.tab_switches} tab switches</span>
                  : <span style={{ color: "#9ca3af" }}>No tab switches</span>
                }
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "#f3f4f6", border: "none", cursor: "pointer", color: "#6b7280", borderRadius: 8, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={16} />
          </button>
        </div>

        {/* Tab bar */}
        <div style={{ display: "flex", gap: 0, padding: "16px 28px 0", borderBottom: "1px solid #e5e7eb", marginTop: 16 }}>
          {[
            { key: "auto",   label: `Auto-Graded (${autoAnswers.length})`,    desc: "MCQ · T/F · Fill Blank · Match" },
            { key: "manual", label: `Manual Review (${manualAnswers.length})`, desc: "Long Answer · Short Answer" },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: "10px 20px 12px", border: "none", background: "none", cursor: "pointer",
              borderBottom: `3px solid ${tab === t.key ? "#4f46e5" : "transparent"}`,
              color: tab === t.key ? "#4f46e5" : "#6b7280",
              fontWeight: tab === t.key ? 700 : 500, fontSize: 13,
              display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2,
            }}>
              <span>{t.label}</span>
              <span style={{ fontSize: 10, color: tab === t.key ? "#a5b4fc" : "#9ca3af", fontWeight: 400 }}>{t.desc}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
            <Loader size={28} className="ms2-spin" color="#4f46e5" />
          </div>
        ) : (
          <div style={{ maxHeight: "62vh", overflow: "auto", padding: "24px 28px" }}>

            {/* ── AUTO-GRADED TAB ── */}
            {tab === "auto" && (
              <>
                {/* Summary bar */}
                <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
                  {[
                    { label: "Correct", value: autoCorrect, bg: "#f0fdf4", color: "#166534", border: "#bbf7d0" },
                    { label: "Wrong",   value: autoWrong,   bg: "#fef2f2", color: "#991b1b", border: "#fecaca" },
                    { label: "Skipped", value: autoSkipped, bg: "#f8fafc", color: "#6b7280",  border: "#e5e7eb" },
                    { label: "Total",   value: autoAnswers.length, bg: "#ede9fe", color: "#4f46e5", border: "#c4b5fd" },
                  ].map(s => (
                    <div key={s.label} style={{ flex: 1, background: s.bg, border: `1.5px solid ${s.border}`, borderRadius: 10, padding: "10px 14px", textAlign: "center" }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: s.color, opacity: 0.8, marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {autoAnswers.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af", fontSize: 14 }}>No auto-graded questions.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {autoAnswers.map((ans, i) => {
                      const isCorrect = ans.is_correct === 1;
                      const skipped   = !ans.option_id && !ans.text_answer;
                      return (
                        <div key={ans.id} style={{ border: `1.5px solid ${skipped ? "#e5e7eb" : isCorrect ? "#bbf7d0" : "#fecaca"}`, borderRadius: 12, overflow: "hidden" }}>
                          {/* Card header */}
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", background: skipped ? "#f8fafc" : isCorrect ? "#f0fdf4" : "#fef2f2", borderBottom: "1px solid #e5e7eb" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ width: 26, height: 26, borderRadius: 8, background: "#4f46e5", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: "#fff", flexShrink: 0 }}>
                                Q{i + 1}
                              </span>
                              <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280" }}>
                                {ans.section_label} · {ans.question_type?.replace(/_/g, " ").toUpperCase()}
                              </span>
                              <span style={{ fontSize: 11, color: "#9ca3af" }}>[{ans.question_marks} marks]</span>
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 20,
                              background: skipped ? "#f3f4f6" : isCorrect ? "#dcfce7" : "#fee2e2",
                              color: skipped ? "#6b7280" : isCorrect ? "#166534" : "#991b1b" }}>
                              {skipped ? "Skipped" : isCorrect ? "✓ Correct" : "✗ Wrong"}
                            </span>
                          </div>
                          {/* Question + answer */}
                          <div style={{ padding: "12px 16px" }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginBottom: 10, lineHeight: 1.6 }}>
                              {ans.question_type === "match_following"
                                ? (() => { try { return JSON.parse(ans.question_text || "{}").instruction || "Match the Following"; } catch { return ans.question_text; } })()
                                : ans.question_text}
                            </div>
                            {ans.question_type === "match_following"
                              ? renderMatchAnswer(ans)
                              : (
                                <div style={{ display: "flex", gap: 10 }}>
                                  {ans.selected_option_text && (
                                    <div style={{ flex: 1, background: "#f8fafc", borderRadius: 8, padding: "8px 12px", fontSize: 13, border: "1px solid #e5e7eb" }}>
                                      <span style={{ fontSize: 10, fontWeight: 800, color: "#6b7280", display: "block", marginBottom: 4 }}>STUDENT ANSWER</span>
                                      <span style={{ color: "#374151" }}>{ans.selected_option_text || ans.text_answer || <em style={{ color: "#9ca3af" }}>No answer</em>}</span>
                                    </div>
                                  )}
                                  {ans.text_answer && !ans.selected_option_text && (
                                    <div style={{ flex: 1, background: "#f8fafc", borderRadius: 8, padding: "8px 12px", fontSize: 13, border: "1px solid #e5e7eb" }}>
                                      <span style={{ fontSize: 10, fontWeight: 800, color: "#6b7280", display: "block", marginBottom: 4 }}>STUDENT ANSWER</span>
                                      <span style={{ color: "#374151" }}>{ans.text_answer}</span>
                                    </div>
                                  )}
                                  {ans.correct_option_text && !isCorrect && (
                                    <div style={{ flex: 1, background: "#f0fdf4", borderRadius: 8, padding: "8px 12px", fontSize: 13, border: "1px solid #bbf7d0" }}>
                                      <span style={{ fontSize: 10, fontWeight: 800, color: "#166534", display: "block", marginBottom: 4 }}>CORRECT ANSWER</span>
                                      <span style={{ color: "#166534", fontWeight: 600 }}>{ans.correct_option_text}</span>
                                    </div>
                                  )}
                                </div>
                              )
                            }
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* ── MANUAL REVIEW TAB ── */}
            {tab === "manual" && (
              <>
                {manualAnswers.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af" }}>
                    <ClipboardList size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
                    <div style={{ fontSize: 14 }}>No long/short answer questions to review.</div>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    {manualAnswers.map((ans, i) => {
                      const graded = grades[ans.id];
                      const alreadySaved = ans.marks_awarded != null;
                      return (
                        <div key={ans.id} style={{ border: "1.5px solid #e5e7eb", borderRadius: 14, overflow: "hidden" }}>
                          {/* Card header */}
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 18px", background: "linear-gradient(135deg,#4f46e508,#7c3aed08)", borderBottom: "1px solid #e5e7eb" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ width: 30, height: 30, borderRadius: 9, background: "linear-gradient(135deg,#4f46e5,#7c3aed)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: "#fff", flexShrink: 0 }}>
                                Q{i + 1}
                              </span>
                              <div>
                                <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280" }}>{ans.section_label}</span>
                                <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 8 }}>{ans.question_type?.replace(/_/g, " ").toUpperCase()}</span>
                              </div>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontSize: 12, color: "#9ca3af" }}>Max: <strong style={{ color: "#374151" }}>{ans.question_marks}</strong> marks</span>
                              {alreadySaved && (
                                <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20, background: "#dbeafe", color: "#1e40af" }}>
                                  Graded: {ans.marks_awarded}
                                </span>
                              )}
                            </div>
                          </div>

                          <div style={{ padding: "16px 18px" }}>
                            {/* Question */}
                            <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", marginBottom: 14, lineHeight: 1.7 }}>
                              {ans.question_text}
                            </div>

                            {/* Student answer */}
                            <div style={{ background: "#fffbeb", borderRadius: 10, padding: "12px 16px", marginBottom: 16, border: "1.5px solid #fde68a" }}>
                              <div style={{ fontSize: 10, fontWeight: 800, color: "#92400e", marginBottom: 6, letterSpacing: 1 }}>STUDENT ANSWER</div>
                              <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
                                {ans.text_answer || <em style={{ color: "#9ca3af" }}>No answer provided</em>}
                              </div>
                            </div>

                            {/* Grading controls */}
                            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "12px 16px", background: "#f8fafc", borderRadius: 10, border: "1px solid #e5e7eb" }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginRight: 4 }}>Mark as:</span>
                              <button onClick={() => {
                                const updated = { ...graded, is_correct: 1 };
                                setGrades(g => ({ ...g, [ans.id]: updated }));
                                handleGrade(ans.id, { is_correct: 1, marks_awarded: graded?.marks_awarded });
                              }} style={{
                                padding: "6px 14px", borderRadius: 8, border: "1.5px solid",
                                borderColor: graded?.is_correct === 1 ? "#16a34a" : "#d1d5db",
                                background: graded?.is_correct === 1 ? "#dcfce7" : "#fff",
                                color: graded?.is_correct === 1 ? "#166534" : "#6b7280",
                                fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
                              }}>
                                <CheckCircle size={13} /> Correct
                              </button>
                              <button onClick={() => {
                                const updated = { ...graded, is_correct: 0 };
                                setGrades(g => ({ ...g, [ans.id]: updated }));
                                handleGrade(ans.id, { is_correct: 0, marks_awarded: graded?.marks_awarded });
                              }} style={{
                                padding: "6px 14px", borderRadius: 8, border: "1.5px solid",
                                borderColor: graded?.is_correct === 0 ? "#dc2626" : "#d1d5db",
                                background: graded?.is_correct === 0 ? "#fee2e2" : "#fff",
                                color: graded?.is_correct === 0 ? "#991b1b" : "#6b7280",
                                fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
                              }}>
                                <X size={13} /> Wrong
                              </button>
                              <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
                                <span style={{ fontSize: 12, color: "#6b7280" }}>Marks:</span>
                                <input
                                  type="number" min="0" max={ans.question_marks} step="0.5"
                                  value={graded?.marks_awarded ?? ""}
                                  onChange={e => setGrades(g => ({ ...g, [ans.id]: { ...g[ans.id], marks_awarded: e.target.value } }))}
                                  onBlur={() => handleGrade(ans.id)}
                                  placeholder="0"
                                  style={{ width: 70, padding: "6px 10px", borderRadius: 8, border: "1.5px solid #d1d5db", fontSize: 14, fontWeight: 700, textAlign: "center", outline: "none" }}
                                />
                                <span style={{ fontSize: 12, color: "#9ca3af" }}>/ {ans.question_marks}</span>
                                {saving[ans.id] && <Loader size={14} className="ms2-spin" color="#4f46e5" />}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

          </div>
        )}
      </div>
    </div>
  );
}

/* ── MAIN PAGE ── */
export default function OnlineExamResults() {
  const { examId } = useParams();
  const nav = useNavigate();
  const userData = getUser();
  const schoolId = userData?.admin_id || null;

  const [exam,        setExam]        = useState(null);
  const [attempts,    setAttempts]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [gradeAtt,    setGradeAtt]    = useState(null);
  const [toggling,    setToggling]    = useState(false);
  const [recalcing,   setRecalcing]   = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [examRes, attRes] = await Promise.all([
        lmsGetExamById(examId),
        oeGetExamAttempts(examId, { school_id: schoolId }),
      ]);
      setExam(examRes.data);
      setAttempts(attRes.data.data || []);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [examId]);

  const handleToggleVisibility = async () => {
    setToggling(true);
    try {
      const next = exam.allow_result_view ? 0 : 1;
      await oeToggleResultVisibility(examId, { allow_result_view: next });
      setExam(e => ({ ...e, allow_result_view: next }));
      toast.success(next ? "Results published to students" : "Results hidden from students");
    } catch { toast.error("Failed to update visibility"); }
    finally { setToggling(false); }
  };

  // Stats
  const submitted = attempts.filter(a => a.status === "submitted" || a.status === "graded").length;
  const passed    = attempts.filter(a => a.pass_fail === "pass").length;
  const avgScore  = submitted > 0
    ? (attempts.reduce((s, a) => s + (Number(a.score) || 0), 0) / submitted).toFixed(1)
    : "—";

  return (
    <div className="lms-content-inner">
      {/* Header */}
      <div style={{ background: "#fff", borderRadius: 14, padding: "20px 24px", marginBottom: 20, border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => nav(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", display: "flex", alignItems: "center", gap: 5, fontSize: 13, fontWeight: 600 }}>
            <ChevronLeft size={16} /> Back
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 18, color: "#111827" }}>{exam?.name || "Exam Results"}</div>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
              {exam?.class_name}{exam?.subject_name ? ` · ${exam?.subject_name}` : ""} · Online Exam
            </div>
          </div>
          {/* Result visibility toggle */}
          <button onClick={handleToggleVisibility} disabled={toggling} style={{
            display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", borderRadius: 9,
            border: `1.5px solid ${exam?.allow_result_view ? "#fca5a5" : "#bbf7d0"}`,
            background: exam?.allow_result_view ? "#fef2f2" : "#f0fdf4",
            color: exam?.allow_result_view ? "#dc2626" : "#16a34a",
            fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}>
            {exam?.allow_result_view ? <EyeOff size={14} /> : <Eye size={14} />}
            {exam?.allow_result_view ? "Hide Results" : "Publish Results"}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Total Attempts", value: attempts.length, icon: Users, color: "#4f46e5" },
          { label: "Submitted",      value: submitted,        icon: CheckCircle, color: "#16a34a" },
          { label: "Passed",         value: passed,           icon: BarChart2,   color: "#0891b2" },
          { label: "Avg Score",      value: avgScore,         icon: ClipboardList, color: "#d97706" },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} style={{ background: "#fff", borderRadius: 12, padding: "16px", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: color + "18", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon size={18} color={color} />
            </div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#111827" }}>{value}</div>
              <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600 }}>{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Attempts table */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb", overflow: "hidden" }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid #e5e7eb", fontWeight: 700, fontSize: 14, color: "#374151" }}>
          Student Attempts ({attempts.length})
        </div>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
            <Loader size={28} className="ms2-spin" color="#4f46e5" />
          </div>
        ) : attempts.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#9ca3af", fontSize: 14 }}>
            No students have attempted this exam yet.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  {["Student", "Roll No", "Status", "Score", "%", "Pass/Fail", "Time", "Tab Switches", "Action"].map(h => (
                    <th key={h} style={{ padding: "10px 14px", fontSize: 11, fontWeight: 700, color: "#9ca3af", textAlign: "left", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {attempts.map(att => (
                  <AttemptRow key={att.id} att={att} onView={setGradeAtt}
                    recalcing={recalcing}
                    onRecalc={async (a) => {
                      setRecalcing(a.id);
                      try {
                        await oeRecalculateAttempt(a.id);
                        toast.success("Score recalculated");
                        load();
                      } catch { toast.error("Recalculate failed"); }
                      finally { setRecalcing(null); }
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Grading Modal */}
      {gradeAtt && (
        <GradingModal
          attempt={gradeAtt}
          onClose={() => setGradeAtt(null)}
          onSaved={() => { load(); }}
        />
      )}
    </div>
  );
}
